import { Core } from "@faire/design-tokens";
import { BaseFiniteScrollCarousel } from "@faire/retailer-visitor-shared/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { SpacerMultipliersKey } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/web--source/slate/Theme/ResponsiveProp";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { ReactNode, useRef, useState } from "react";
import styled, { css } from "styled-components";

const FiniteScrollCarouselBase = <T,>(
  props: FiniteScrollCarousel.IProps<T>
) => {
  const [isLeftEndReached, setIsLeftEndReached] = useState(true);
  const [isRightEndReached, setIsRightEndReached] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [carouselWidth, setCarouselWidth] = useState("100vw");

  const scrollRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();
  const {
    itemsPerSlide,
    peekGapPixel = 0,
    peekGapPercentage = 0,
    itemGap,
    renderItem,
    items,
    className,
    latentPadding,
    onVisible,
  } = props;

  React.useEffect(() => {
    if (scrollRef.current) {
      const width = scrollRef.current.getBoundingClientRect().width;
      setIsOverflow(scrollRef.current.scrollWidth > Math.round(width));
      setCarouselWidth(`${width}px`);
    } else {
      setIsOverflow(false);
      setCarouselWidth("100vw");
    }
  }, [scrollRef, scrollRef.current?.scrollWidth]);

  const renderContentItem = (item: T, index: number) => {
    return (
      <ContentItem
        key={index}
        $itemGap={itemGap}
        $itemsPerSlide={realItemsPerSlide}
        $peekGapPixel={realPeekGapPixel}
        $peekGapPercentage={realPeekGapPercentage}
        $carouselWidth={carouselWidth}
      >
        {renderItem(item, index)}
      </ContentItem>
    );
  };

  const onRightEndReached = (inputRightEndReached: boolean) => {
    setIsRightEndReached(inputRightEndReached);
  };

  const onLeftEndReached = (inputLeftEndReached: boolean) => {
    setIsLeftEndReached(inputLeftEndReached);
  };

  const shouldHidePreviousButton =
    !isOverflow || (!!props.hideWhenEndReached && isLeftEndReached);

  const shouldHideNextButton =
    !isOverflow || (!!props.hideWhenEndReached && isRightEndReached);

  const realItemsPerSlide = isResponsiveProp(itemsPerSlide)
    ? itemsPerSlide
    : { mobileAndAbove: itemsPerSlide };

  const realPeekGapPixel = isResponsiveProp(peekGapPixel)
    ? peekGapPixel
    : { mobileAndAbove: peekGapPixel };

  const realPeekGapPercentage = isResponsiveProp(peekGapPercentage)
    ? peekGapPercentage
    : { mobileAndAbove: peekGapPercentage };

  return (
    <StyledBaseFiniteScrollCarousel
      forwardRef={scrollRef}
      name={props.name}
      shouldHideLeftButton={shouldHidePreviousButton}
      shouldHideRightButton={shouldHideNextButton}
      isLeftMostReached={isLeftEndReached}
      isRightMostReached={isRightEndReached}
      onRightEndReached={onRightEndReached}
      onLeftEndReached={onLeftEndReached}
      onVisible={onVisible}
      peekGapPixel={useResponsiveValue(realPeekGapPixel)}
      peekGapPercentage={useResponsiveValue(realPeekGapPercentage)}
      className={className}
      enableDrag
    >
      <RowWrapper>
        {items.map(renderContentItem)}
        {latentPadding ? <Spacer size={latentPadding} /> : null}
        {isMobile && !latentPadding ? <Spacer size="2x" /> : null}
      </RowWrapper>
    </StyledBaseFiniteScrollCarousel>
  );
};

const StyledBaseFiniteScrollCarousel = styled(
  BaseFiniteScrollCarousel.BaseFiniteScrollCarousel
)<{
  isLeftMostReached?: boolean;
  isRightMostReached?: boolean;
}>`
  ${BaseFiniteScrollCarousel.Previous} {
    color: ${({ isLeftMostReached }) =>
      isLeftMostReached ? Core.text.subdued : Core.text.enabled};
  }

  ${BaseFiniteScrollCarousel.Next} {
    color: ${({ isRightMostReached }) =>
      isRightMostReached ? Core.text.subdued : Core.text.enabled};
  }
`;

const ContentItem = styled.div<{
  $itemsPerSlide: ResponsiveValues<number>;
  $itemGap: number;
  $peekGapPixel: ResponsiveValues<number>;
  $peekGapPercentage: ResponsiveValues<number>;
  $carouselWidth: string;
}>`
  flex-shrink: 0;
  padding: 0 ${(props) => props.$itemGap / 2}px;
  width: ${(props) => {
    const peekGapPixel = props.$peekGapPixel.mobileAndAbove;
    const peekGapPercentage = props.$peekGapPercentage.mobileAndAbove;
    const itemsPerSlide = props.$itemsPerSlide.mobileAndAbove;

    return css`calc((100% - ${peekGapPixel}px - ${peekGapPercentage}%) / ${itemsPerSlide})`;
  }};

  ${responsiveMediaQueries.tabletAndAbove} {
    width: ${(props) => {
      const peekGapPixel =
        props.$peekGapPixel.tabletAndAbove ??
        props.$peekGapPixel.mobileAndAbove;
      const peekGapPercentage =
        props.$peekGapPercentage.tabletAndAbove ??
        props.$peekGapPercentage.mobileAndAbove;
      const itemsPerSlide =
        props.$itemsPerSlide.tabletAndAbove ??
        props.$itemsPerSlide.mobileAndAbove;

      return css`calc((100% - ${peekGapPixel}px - ${peekGapPercentage}%) / ${itemsPerSlide})`;
    }};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: ${(props) => {
      const peekGapPixel =
        props.$peekGapPixel.desktopAndAbove ??
        props.$peekGapPixel.tabletAndAbove ??
        props.$peekGapPixel.mobileAndAbove;
      const peekGapPercentage =
        props.$peekGapPercentage.desktopAndAbove ??
        props.$peekGapPercentage.tabletAndAbove ??
        props.$peekGapPercentage.mobileAndAbove;
      const itemsPerSlide =
        props.$itemsPerSlide.desktopAndAbove ??
        props.$itemsPerSlide.tabletAndAbove ??
        props.$itemsPerSlide.mobileAndAbove;

      return css`calc((100% - ${peekGapPixel}px - ${peekGapPercentage}%) / ${itemsPerSlide})`;
    }};
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    width: ${(props) => {
      const peekGapPixel =
        props.$peekGapPixel.xLargeAndAbove ??
        props.$peekGapPixel.desktopAndAbove ??
        props.$peekGapPixel.tabletAndAbove ??
        props.$peekGapPixel.mobileAndAbove;
      const peekGapPercentage =
        props.$peekGapPercentage.xLargeAndAbove ??
        props.$peekGapPercentage.desktopAndAbove ??
        props.$peekGapPercentage.tabletAndAbove ??
        props.$peekGapPercentage.mobileAndAbove;
      const itemsPerSlide =
        props.$itemsPerSlide.xLargeAndAbove ??
        props.$itemsPerSlide.desktopAndAbove ??
        props.$itemsPerSlide.tabletAndAbove ??
        props.$itemsPerSlide.mobileAndAbove;

      return css`calc((100% - ${peekGapPixel}px - ${peekGapPercentage}%) / ${itemsPerSlide})`;
    }};
  }
`;

const RowWrapper = styled(Row)`
  width: 100%;
`;

export namespace FiniteScrollCarousel {
  export const SingleItemWrapper = ContentItem;
  export const FiniteScrollCarousel = FiniteScrollCarouselBase;
  export interface IProps<T> {
    itemGap: number;
    itemsPerSlide: ResponsiveProp<number>;
    items: T[];
    renderItem: (item: T, index: number) => ReactNode;
    peekGapPixel?: ResponsiveProp<number>;
    peekGapPercentage?: ResponsiveProp<number>;
    className?: string;
    name: string;
    hideWhenEndReached?: boolean;
    latentPadding?: ResponsiveValues<SpacerMultipliersKey | number>;
    onVisible?: (instance: Element | null | undefined) => void;
  }
}
